import { PayCycle } from '@aslanpay/portal-common-lib';

export type PayCycleStage = 'UPCOMING' | 'CURRENT' | 'CUT_OFF' | 'PAST';

export const PAY_CYCLE_TIMEZONE = 'Europe/London';

const UK_DAY_FORMAT = new Intl.DateTimeFormat('en-CA', {
  timeZone: PAY_CYCLE_TIMEZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/**
 * @description The calendar day, as yyyy-MM-dd, that the given moment falls on in UK time, which every pay calendar runs
 * to, whatever the browser's own timezone.
 */
export const toUkDay = (moment: Date): string => UK_DAY_FORMAT.format(moment);

/**
 * @description The calendar day, as yyyy-MM-dd, of a pay cycle date.
 */
const dayOf = (date: string) => date.slice(0, 10);

/**
 * @description Where a pay cycle is relative to today in UK time: upcoming before it starts, current until its cut-off,
 * cut off from its cut-off until payday while its deductions are being reported, and past once it has been paid.
 */
export const getPayCycleStage = (
  payCycle?: Pick<PayCycle, 'startDate' | 'cutoffDate' | 'payday'>,
  now: Date = new Date()
): PayCycleStage | undefined => {
  if (!payCycle?.startDate) {
    return undefined;
  }

  const today = toUkDay(now);

  if (today < dayOf(payCycle.startDate)) {
    return 'UPCOMING';
  }

  if (payCycle.cutoffDate && today < dayOf(payCycle.cutoffDate)) {
    return 'CURRENT';
  }

  if (payCycle.payday && today <= dayOf(payCycle.payday)) {
    return 'CUT_OFF';
  }

  return payCycle.payday ? 'PAST' : 'CURRENT';
};

export default getPayCycleStage;
